/**
 * Offline scan queue for the check-in scanner.
 *
 * Community centre wifi is unreliable and a check-in queue that stalls at the
 * door is a visible failure with a line of people watching. Every scan is
 * written here first and sent afterwards, so the operator never waits on the
 * network to see a result.
 *
 * Kept deliberately separate from the scanner UI: this is the part that must be
 * correct, and it is fully testable without a camera.
 *
 * Storage is localStorage — scans must survive a tab crash or an accidental
 * refresh mid-event, which in-memory state would not.
 */

const KEY = "ivem.scanQueue.v1";

/** Unique per scan. Sent to the server so a retry is recognised, not recounted. */
export function newScanId() {
  return `scan_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Corrupt or unavailable storage must not take the scanner down.
    return [];
  }
}

function write(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
    return true;
  } catch {
    return false;
  }
}

/** Queue one scan. Returns the stored entry. */
export function enqueue({ eventId, code, dayNumber = null }) {
  const entry = {
    client_scan_id: newScanId(),
    event_id: eventId,
    code: String(code || "").toUpperCase().trim(),
    day_number: dayNumber,
    queued_at: new Date().toISOString(),
  };
  write([...read(), entry]);
  return entry;
}

/** Everything still waiting, oldest first. */
export function pending(eventId = null) {
  const all = read();
  return eventId ? all.filter((s) => s.event_id === eventId) : all;
}

export function pendingCount(eventId = null) {
  return pending(eventId).length;
}

/** Drop specific scans once the server has confirmed them. */
export function resolve(scanIds) {
  const done = new Set(scanIds);
  write(read().filter((s) => !done.has(s.client_scan_id)));
}

export function clear(eventId = null) {
  write(eventId ? read().filter((s) => s.event_id !== eventId) : []);
}

/**
 * Send everything queued for one event.
 *
 * `send` is injected rather than imported so this module stays free of axios
 * and stays testable. It receives the batch payload and should resolve with the
 * server's response.
 *
 * Scans are only dropped from the queue on a successful round trip. A failure
 * leaves them exactly where they were — losing a scan is far worse than sending
 * it twice, which the server already handles.
 */
export async function flush(eventId, send) {
  const batch = pending(eventId);
  if (batch.length === 0) {
    return { flushed: 0, remaining: 0, results: [] };
  }
  const payload = {
    scans: batch.map(({ code, day_number, client_scan_id }) => ({
      code,
      day_number,
      client_scan_id,
    })),
  };
  const response = await send(payload);
  resolve(batch.map((s) => s.client_scan_id));
  return {
    flushed: batch.length,
    remaining: pendingCount(eventId),
    results: response?.results || [],
    accepted: response?.accepted ?? 0,
    duplicates: response?.duplicates ?? 0,
    notFound: response?.not_found ?? 0,
  };
}
